import { useEffect, useRef } from "react"
import { useAppDispatch } from "../../../../reduxStore/hooks"
import {
  ariaLiveMessageSet,
  AriaLivePoliteness,
} from "../../../ariaLive/ariaLiveSlice"
import { SortDirection, TableHeader } from "./types"
import { useCMSReady } from "../../../../domain/CMS/useCMSReady"
import { populateCmsTemplate } from "../../utils"

interface UseTableAriaLiveParams {
  loadingEff: boolean
  totalResultsMessage: string
  sortState: { col: number | null; dir: SortDirection | null }
  headers: TableHeader[]
  paginationAriaId: string
  sortAriaId: string
}

export function useTableAriaLive({
  loadingEff,
  totalResultsMessage,
  sortState,
  headers,
  paginationAriaId,
  sortAriaId,
}: UseTableAriaLiveParams): void {
  const dispatch = useAppDispatch()
  const {
    cms: { common: commonText },
  } = useCMSReady(["common"])

  const wasLoadingRef = useRef(false)
  useEffect(() => {
    if (loadingEff) {
      wasLoadingRef.current = true
      dispatch(
        ariaLiveMessageSet({
          id: paginationAriaId,
          message:
            commonText?.tableLoadingAriaLiveText ??
            "Loading table data, please wait.",
          politeness: AriaLivePoliteness.Polite,
        }),
      )
    } else if (wasLoadingRef.current) {
      wasLoadingRef.current = false
      dispatch(
        ariaLiveMessageSet({
          id: paginationAriaId,
          message: totalResultsMessage,
          politeness: AriaLivePoliteness.Polite,
        }),
      )
    }
  }, [
    loadingEff,
    dispatch,
    paginationAriaId,
    totalResultsMessage,
    commonText?.tableLoadingAriaLiveText,
  ])

  const prevSortedColNameRef = useRef<string | null>(null)
  useEffect(() => {
    if (sortState.col !== null) {
      const columnName = headers[sortState.col]?.label ?? "column"
      prevSortedColNameRef.current = columnName
      const direction =
        sortState.dir === SortDirection.ASC
          ? (commonText?.tableSortAscendingText ?? "ascending, A to Z")
          : (commonText?.tableSortDescendingText ?? "descending, Z to A")
      const sortedMessage =
        populateCmsTemplate(
          [columnName, direction],
          "cms.common.tableSortedAriaLiveTemplate",
          commonText?.tableSortedAriaLiveTemplate,
        ) || `${columnName} column sorted ${direction}.`
      dispatch(
        ariaLiveMessageSet({
          id: sortAriaId,
          message: sortedMessage,
          politeness: AriaLivePoliteness.Polite,
        }),
      )
    } else if (prevSortedColNameRef.current !== null) {
      const columnName = prevSortedColNameRef.current
      prevSortedColNameRef.current = null
      const sortRemovedMessage =
        populateCmsTemplate(
          [columnName],
          "cms.common.tableSortRemovedAriaLiveTemplate",
          commonText?.tableSortRemovedAriaLiveTemplate,
        ) || `${columnName} column sort removed.`
      dispatch(
        ariaLiveMessageSet({
          id: sortAriaId,
          message: sortRemovedMessage,
          politeness: AriaLivePoliteness.Polite,
        }),
      )
    }
  }, [
    sortState.col,
    sortState.dir,
    dispatch,
    sortAriaId,
    headers,
    commonText?.tableSortAscendingText,
    commonText?.tableSortDescendingText,
    commonText?.tableSortedAriaLiveTemplate,
    commonText?.tableSortRemovedAriaLiveTemplate,
  ])
}
